/**
 * How Management ▸ Cycles ▸ Findings groups the in-season run's findings — `?by=`.
 *
 * Issue 226 (giro 3 «Dopo» point 5, owner ruling 2026-10-07). «Raggruppa per: Reparto · Negozio»,
 * the same `GroupByControl` as Network health and the grouped alerts (ADR 9042):
 *
 * - absent, empty, `department` or anything this build does not know: by department family, the
 *   table the page has always drawn — every earlier link opens unchanged;
 * - `store`: the digest's two store rankings (most units proposed, most unmet need) merged into one
 *   table whose columns sort. The digest publishes only its top {@link DIGEST_TOP} of each, so the
 *   table says so (ADR 9034); no new read is made.
 *
 * Only the in-season module groups: with `?module=exit` the exit digest publishes lanes by reason
 * and residual by door role, neither by department nor by store, so the control is not drawn and
 * `?by=` is ignored (and kept, so switching back restores it). Changing the grouping keeps the run:
 * it is the same run read another way. Pure parse, so a test and the export read a string.
 */

import { navigateToScope } from './scope';

export const CYCLE_GROUP_PARAM = 'by';

export type CycleGrouping = 'department' | 'store';

/** The groupings in the control's order. */
export const CYCLE_GROUPINGS: readonly CycleGrouping[] = ['department', 'store'];

/** The grouping a search string names; anything but `store` is by department. Pure. */
export function readCycleGrouping(search: string): CycleGrouping {
  return new URLSearchParams(search).get(CYCLE_GROUP_PARAM)?.trim() === 'store' ? 'store' : 'department';
}

/** The search a grouping choice navigates to: `?by=store`, or no `by` for the default. Pure. */
export function cycleGroupingSearch(search: string, grouping: CycleGrouping): URLSearchParams {
  const next = new URLSearchParams(search);
  if (grouping === 'store') next.set(CYCLE_GROUP_PARAM, 'store');
  else next.delete(CYCLE_GROUP_PARAM);
  return next;
}

/** The planner chose a grouping: a history step, so Back returns to the one they left. */
export function chooseCycleGrouping(grouping: CycleGrouping): void {
  if (typeof window === 'undefined') return;
  navigateToScope(cycleGroupingSearch(window.location.search, grouping));
}
